import type { IUpdateTenant } from '@escloud/types';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { userKeys } from './use-current-user';

import { useApi, useTenant } from '@/context';

export const tenantKeys = {
    all: ['tenants'] as const,
    detail: (id: string) => [...tenantKeys.all, id] as const,
};

/**
 * Fetch details of the tenant currently selected in TenantContext.
 * The server resolves the tenant exclusively from the X-Tenant-Id header,
 * which ApiClient adds automatically from TenantContext.
 */
export function useCurrentTenantDetails() {
    const api = useApi();
    const { tenantId } = useTenant();

    return useQuery({
        queryKey: tenantKeys.detail(tenantId!),
        queryFn: () => api.tenants.getCurrentTenant(),
        enabled: !!tenantId,
    });
}

/**
 * Update the tenant currently selected in TenantContext.
 * The server resolves the tenant exclusively from the X-Tenant-Id header.
 */
export function useUpdateCurrentTenant() {
    const api = useApi();
    const queryClient = useQueryClient();
    const { tenantId } = useTenant();

    return useMutation({
        mutationFn: (data: IUpdateTenant) => api.tenants.updateCurrentTenant(data),
        onSuccess: (updatedTenant) => {
            if (tenantId) {
                queryClient.setQueryData(tenantKeys.detail(tenantId), updatedTenant);
            }
            queryClient.invalidateQueries({ queryKey: userKeys.tenants() });
        },
    });
}
